import React from "react";
import { AlertTriangle, RefreshCw, ArrowLeft } from "lucide-react";

/**
 * Global error boundary. If any child component throws during render,
 * we show a friendly error card instead of a blank white page.
 * The full error is still logged to the console for debugging.
 */
export class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, info: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error("[Transport.pro] Uncaught render error:", error, info);
    this.setState({ info });
  }

  handleReload = () => {
    window.location.reload();
  };

  handleGoBack = () => {
    window.location.href = "/";
  };

  render() {
    if (!this.state.hasError) return this.props.children;
    const { error, info } = this.state;
    return (
      <div className="min-h-screen grid place-items-center bg-slate-50 dark:bg-slate-950 px-4">
        <div className="max-w-lg w-full bg-white dark:bg-slate-900 rounded-2xl shadow-lg border border-slate-200 dark:border-slate-800 p-8 space-y-5" data-testid="app-error-boundary">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-red-100 dark:bg-red-500/20 grid place-items-center">
              <AlertTriangle className="w-6 h-6 text-red-600 dark:text-red-400" />
            </div>
            <div>
              <div className="text-xs uppercase tracking-widest text-red-500 font-semibold">Erreur</div>
              <h1 className="font-display text-xl font-bold">Une erreur inattendue est survenue</h1>
            </div>
          </div>

          <p className="text-sm text-slate-600 dark:text-slate-400">
            Cette page n'a pas pu s'afficher correctement. Vous pouvez recharger la page ou revenir à l'accueil.
            Si le problème persiste, contactez le support.
          </p>

          <div className="flex flex-col sm:flex-row gap-2">
            <button
              onClick={this.handleReload}
              data-testid="error-reload-btn"
              className="btn-pill inline-flex items-center justify-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-semibold px-4 py-2.5 rounded-full transition-colors"
            >
              <RefreshCw className="w-4 h-4" /> Recharger la page
            </button>
            <button
              onClick={this.handleGoBack}
              data-testid="error-home-btn"
              className="btn-pill inline-flex items-center justify-center gap-2 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-800 dark:text-slate-200 font-semibold px-4 py-2.5 rounded-full transition-colors"
            >
              <ArrowLeft className="w-4 h-4" /> Retour à l'accueil
            </button>
          </div>

          {process.env.NODE_ENV !== "production" && error && (
            <details className="text-xs">
              <summary className="cursor-pointer text-slate-500 hover:text-slate-700 dark:hover:text-slate-300">Détails techniques</summary>
              <pre className="mt-2 p-3 bg-slate-100 dark:bg-slate-950 rounded-lg overflow-x-auto text-red-600 dark:text-red-400 whitespace-pre-wrap">
                {String(error?.message || error)}
                {info?.componentStack ? `\n\nComponent stack:${info.componentStack}` : ""}
              </pre>
            </details>
          )}
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
